import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { create } from "@/lib/store";
import { SalesPeriod } from "@/pages/sales-drivers/api/getSalesCompositionV2";
import { BusinessUnitToggle, GeoViewToggle, MetricType } from "@/shared/types";
import { hashStorage } from "@/shared/utils/hashStorage";

const STORE_NAME = "Sales Drivers";

// Default columns for the sales drivers grid (you may want to adjust these)
const DEFAULT_COLUMNS = ["business_unit", "base_sale", "media_sale", "control_sale", "total"];

interface SalesDriversState {
  filters: {
    targetNodeUUID: string | null;
    geography: string[];
    activeFilterIds: string[];
    expandedSectionIds: string[];
  };
  chart: {
    isDollarViewSelected: boolean;
    period: SalesPeriod;
    isExternalFactorsDescending: boolean;
  };
  grid: {
    geoView: GeoViewToggle;
    businessUnitToggle: BusinessUnitToggle;
    activeColumns: string[];
    metricType: MetricType;
  };
}

interface SalesDriversActions {
  setTargetNode: (nodeUUID: string | null) => void;
  setGeography: (geography: string[]) => void;
  setDurationSwitch: (period: SalesPeriod) => void;
  setIsDollarViewSelected: (isDollarViewSelected: boolean) => void;
  setIsExternalFactorsDescending: (isDescending: boolean) => void;
  setBusinessUnitToggle: (toggle: BusinessUnitToggle) => void;
  setGeoView: (geoView: GeoViewToggle) => void;
  setActiveColumns: (columns: string[]) => void;
  setMetricType: (metricType: MetricType) => void;
  setActiveFilterIds: (ids: string[]) => void;
  setExpandedSectionIds: (ids: string[]) => void;
}

type SalesDriversStore = SalesDriversState & SalesDriversActions;

const defaultState: SalesDriversState = {
  filters: {
    targetNodeUUID: null,
    geography: [],
    activeFilterIds: [],
    expandedSectionIds: [],
  },
  chart: {
    isDollarViewSelected: true,
    period: "week",
    isExternalFactorsDescending: true,
  },
  grid: {
    geoView: GeoViewToggle.AGGREGATE,
    businessUnitToggle: BusinessUnitToggle.AGGREGATE,
    activeColumns: DEFAULT_COLUMNS,
    metricType: MetricType.SALES_REVENUE,
  },
};

export const useSalesDriversStore = create<SalesDriversStore>()(
  devtools(
    persist(
      immer((set) => ({
        ...defaultState,
        setTargetNode: (nodeUUID) =>
          set((state) => {
            state.filters.targetNodeUUID = nodeUUID;
          }),
        setGeography: (geography) =>
          set((state) => {
            state.filters.geography = geography;
          }),
        setDurationSwitch: (period) =>
          set((state) => {
            state.chart.period = period;
          }),
        setIsDollarViewSelected: (isDollarViewSelected) =>
          set((state) => {
            state.chart.isDollarViewSelected = isDollarViewSelected;
          }),
        setIsExternalFactorsDescending: (isDescending) =>
          set((state) => {
            state.chart.isExternalFactorsDescending = isDescending;
          }),
        setBusinessUnitToggle: (businessUnitToggle) =>
          set((state) => {
            state.grid.businessUnitToggle = businessUnitToggle;
          }),
        setGeoView: (geoView) =>
          set((state) => {
            state.grid.geoView = geoView;
          }),
        setActiveColumns: (columns) =>
          set((state) => {
            state.grid.activeColumns = columns;
          }),
        setMetricType: (metricType) =>
          set((state) => {
            state.grid.metricType = metricType;
          }),
        setActiveFilterIds: (ids) =>
          set((state) => {
            state.filters.activeFilterIds = ids;
          }),
        setExpandedSectionIds: (ids) =>
          set((state) => {
            state.filters.expandedSectionIds = ids;
          }),
      })),
      {
        name: STORE_NAME,
        storage: createJSONStorage(() => hashStorage),
      }
    ),
    { name: STORE_NAME }
  )
);
